import React, { useState, useEffect } from 'react';
import SupplierController from '../../controllers/supplier-controller';

const SupplierDetailsForm = ({ formData, onChange, errors }) => {
  const [suppliers, setSuppliers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);

  // Fetch suppliers when component mounts
  useEffect(() => {
    const loadSuppliers = async () => {
      try {
        setIsLoading(true);
        const data = await SupplierController.fetchAllSuppliers();
        setSuppliers(data);
      } catch (error) {
        console.error("Error fetching suppliers:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadSuppliers();
  }, []);

  // When the form data changes and has a supplier ID, select the supplier
  useEffect(() => {
    // Check both possible locations for supplier ID
    const supplierId = formData.supplier?.id || formData.supplierId;

    if (supplierId && suppliers.length > 0) {
      console.log("Looking for supplier with ID:", supplierId);
      console.log("Available suppliers:", suppliers.map(s => ({ id: s.id, name: s.name })));

      // Convert to number for comparison if needed
      const supplierIdNum = typeof supplierId === 'string' ? parseInt(supplierId, 10) : supplierId;

      const selectedSupplier = suppliers.find(s => s.id === supplierIdNum);

      if (selectedSupplier) {
        console.log("Found supplier:", selectedSupplier.name);
        setSelectedSupplier(selectedSupplier);

        onChange({
          target: {
            name: 'supplier.name',
            value: selectedSupplier.name
          }
        });
        onChange({
          target: {
            name: 'supplier.address',
            value: selectedSupplier.adresse || ''
          }
        });
        onChange({
          target: {
            name: 'supplier.phone',
            value: selectedSupplier.tel || ''
          }
        });
        onChange({
          target: {
            name: 'supplier.email',
            value: selectedSupplier.email || ''
          }
        });
      } else {
        console.log("Supplier not found with ID:", supplierId);
      }
    }
  }, [formData.supplier?.id, formData.supplierId, suppliers]);

  // Handle selecting a supplier from the dropdown
  const handleSelectSupplier = (supplier) => {
    setIsDropdownOpen(false);

    // Update all supplier fields
    onChange({
      target: {
        name: 'supplierId',
        value: supplier.id
      }
    });
    
    onChange({
      target: {
        name: 'supplier.name',
        value: supplier.name
      }
    });
    
    onChange({
      target: {
        name: 'supplier.phone',
        value: supplier.telephone || ''
      }
    });
    
    onChange({
      target: {
        name: 'supplier.email',
        value: supplier.email || ''
      }
    });
    
    onChange({
      target: {
        name: 'supplier.address',
        value: supplier.adresse || ''
      }
    });
  };

  return (
    <div className="bg-white overflow-hidden sm:rounded-lg border border-gray-200">
      <div className="px-6 py-5 sm:px-8 bg-white border-b border-gray-200">
        <div className="flex items-center">
          <div className="p-2 rounded-md bg-[#f5f8f7] mr-4">
            <svg className="h-6 w-6 text-[#174540]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg leading-6 font-medium text-gray-900">Détails du Fournisseur</h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Informations sur le fournisseur pour cet achat
            </p>
          </div>
        </div>
      </div>

      <div className="px-6 py-6 sm:px-8 sm:py-7 bg-white">
        <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
          {/* Supplier dropdown */}
          <div className="sm:col-span-6 mb-4">
            <label htmlFor="supplier-select" className="block text-sm font-medium text-gray-700 mb-1">
              Sélectionner un fournisseur <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <div
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="cursor-pointer relative w-full bg-white border border-gray-300 rounded-md pl-3 pr-10 py-2 text-left focus:outline-none focus:ring-1 focus:ring-[#174540] focus:border-[#174540] sm:text-sm"
              >
                <span className="block truncate">
                  {selectedSupplier ? selectedSupplier.name : "Sélectionner un fournisseur"}
                </span>
                <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </span>
              </div>

              {isDropdownOpen && (
                <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                  {isLoading ? (
                    <div className="text-gray-500 px-4 py-2">Chargement...</div>
                  ) : suppliers.length > 0 ? (
                    suppliers.map((supplier) => (
                      <div
                        key={supplier.id}
                        className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-100"
                        onClick={() => handleSelectSupplier(supplier)}
                      >
                        <div className="flex items-center">
                          <span className="font-medium block truncate">{supplier.name}</span>
                        </div>
                        <div className="text-xs text-gray-500 ml-2">
                          {supplier.email && `Email: ${supplier.email}`}
                          {supplier.telephone && ` • Tél: ${supplier.telephone}`}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-gray-500 px-4 py-2">Aucun fournisseur disponible</div>
                  )}
                </div>
              )}
              <input type="hidden" name="supplierId" value={formData.supplierId || ''} />
            </div>
            {errors['supplierId'] && <p className="mt-2 text-sm text-red-600">{errors['supplierId']}</p>}
          </div>

          <div className="sm:col-span-3">
            <label htmlFor="supplier.name" className="block text-sm font-medium text-gray-700 mb-1">
              Nom du fournisseur
            </label>
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <input
                type="text"
                name="supplier.name"
                id="supplier.name"
                value={formData.supplier.name}
                onChange={onChange}
                disabled
                className="pl-10 py-3 shadow-sm block w-full border-0 border-b-2 border-gray-200 bg-gray-50 text-gray-600 text-sm rounded-t-md"
              />
            </div>
          </div>

          <div className="sm:col-span-3">
            <label htmlFor="supplier.phone" className="block text-sm font-medium text-gray-700 mb-1">
              Téléphone
            </label>
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
              <input
                type="text"
                name="supplier.phone"
                id="supplier.phone"
                value={formData.supplier.phone}
                onChange={onChange}
                disabled
                className="pl-10 py-3 shadow-sm block w-full border-0 border-b-2 border-gray-200 bg-gray-50 text-gray-600 text-sm rounded-t-md"
              />
            </div>
          </div>

          <div className="sm:col-span-3">
            <label htmlFor="supplier.email" className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <input
                type="email"
                name="supplier.email"
                id="supplier.email"
                value={formData.supplier.email}
                onChange={onChange}
                disabled
                className="pl-10 py-3 shadow-sm block w-full border-0 border-b-2 border-gray-200 bg-gray-50 text-gray-600 text-sm rounded-t-md"
              />
            </div>
          </div>

          <div className="sm:col-span-3">
            <label htmlFor="supplier.address" className="block text-sm font-medium text-gray-700 mb-1">
              Adresse
            </label>
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
              </div>
              <input
                type="text"
                name="supplier.address"
                id="supplier.address"
                value={formData.supplier.address}
                onChange={onChange}
                disabled
                className="pl-10 py-3 shadow-sm block w-full border-0 border-b-2 border-gray-200 bg-gray-50 text-gray-600 text-sm rounded-t-md"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupplierDetailsForm;